import React, { useEffect, useMemo, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Copy, Gift, Share2, Users, Wallet, Mail, MessageSquare, QrCode, X } from "lucide-react";

export default function ReferralCard() {
  const [data, setData] = useState(null);
  const [mode, setMode] = useState("standard"); // "standard" | "gift"
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteType, setInviteType] = useState(null); // "email" | "sms"
  const [inviteValue, setInviteValue] = useState("");
  const [showQR, setShowQR] = useState(false);

  useEffect(() => {
    api.get("/referrals/me").then((r) => setData(r.data)).catch(() => {});
  }, []);

  const shareUrl = useMemo(() => {
    if (!data?.referral_code) return "";
    const base = `${window.location.origin}/?ref=${data.referral_code}`;
    return mode === "gift" ? `${base}&mode=gift` : base;
  }, [data, mode]);

  const messages = useMemo(() => {
    if (mode === "gift") {
      return {
        title: `Foldora — $${data?.gift_reward || 20} off your first order`,
        text: `I'm gifting you $${data?.gift_reward || 20} off your first Foldora laundry pickup. No pickup, no fold, no fuss. Redeem here: ${shareUrl}`,
      };
    }
    return {
      title: `Foldora — get $${data?.reward_per_referral || 10} off`,
      text: `Try Foldora with me — laundry pickup & delivery in 24h. Use my link and we both get $${data?.reward_per_referral || 10} off: ${shareUrl}`,
    };
  }, [mode, shareUrl, data]);

  if (!data || !data.referral_code) return null;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Referral link copied!");
    } catch {
      toast.error("Copy failed");
    }
  };

  const nativeShare = async () => {
    const shareData = { title: messages.title, text: messages.text, url: shareUrl };
    if (navigator.share) {
      try { await navigator.share(shareData); } catch { /* user cancelled */ }
    } else {
      copy();
    }
  };

  const openEmailInvite = () => {
    setInviteType("email");
    setInviteValue("");
    setInviteOpen(true);
  };
  const openSMSInvite = () => {
    setInviteType("sms");
    setInviteValue("");
    setInviteOpen(true);
  };

  const sendInvite = () => {
    if (!inviteValue.trim()) {
      toast.error(`Please enter ${inviteType === "email" ? "an email" : "a phone number"}`);
      return;
    }
    if (inviteType === "email") {
      const subject = encodeURIComponent(messages.title);
      const body = encodeURIComponent(messages.text);
      window.location.href = `mailto:${inviteValue}?subject=${subject}&body=${body}`;
    } else {
      const body = encodeURIComponent(messages.text);
      // sms: URI works on mobile; on desktop it will attempt to open the default handler.
      window.location.href = `sms:${inviteValue}?body=${body}`;
    }
    setInviteOpen(false);
    toast.success("Invite ready to send!");
  };

  return (
    <Card
      data-testid="referral-card"
      className="p-6 rounded-3xl border-2 border-pink-200 bg-gradient-to-br from-pink-50 to-white mb-6 relative overflow-hidden"
    >
      <div className="absolute -right-6 -top-6 w-32 h-32 rounded-full bg-pink-100 opacity-60" />
      <div className="relative">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-pink-600 mb-2">
              <Gift className="w-3.5 h-3.5" /> Refer &amp; earn
            </div>
            <h2 className="font-heading text-2xl font-black tracking-tighter text-stone-900">
              Share Foldora with friends.
            </h2>
          </div>
          <div className="flex gap-4">
            <Stat icon={<Wallet className="w-4 h-4" />} label="Credits" value={`$${data.credits.toFixed(2)}`} testid="referral-credits" />
            <Stat icon={<Users className="w-4 h-4" />} label="Friends" value={data.referrals_completed} testid="referral-count" />
          </div>
        </div>

        {/* Mode tabs */}
        <div className="mt-6 grid sm:grid-cols-2 gap-3">
          <button
            data-testid="ref-mode-standard"
            type="button"
            onClick={() => setMode("standard")}
            className={`text-left rounded-2xl p-4 border-2 transition-all ${mode === "standard" ? "border-pink-500 bg-white shadow" : "border-transparent bg-white/60 hover:border-pink-200"}`}
          >
            <div className="font-heading font-black text-xl text-stone-900">Give ${data.reward_per_referral}, get ${data.reward_per_referral}</div>
            <div className="text-xs text-stone-600 mt-1">You both get ${data.reward_per_referral} when your friend&rsquo;s first order is delivered.</div>
          </button>
          <button
            data-testid="ref-mode-gift"
            type="button"
            onClick={() => setMode("gift")}
            className={`text-left rounded-2xl p-4 border-2 transition-all ${mode === "gift" ? "border-pink-500 bg-white shadow" : "border-transparent bg-white/60 hover:border-pink-200"}`}
          >
            <div className="font-heading font-black text-xl text-stone-900">Give ${data.gift_reward} to a friend</div>
            <div className="text-xs text-stone-600 mt-1">Share the savings — full ${data.gift_reward} off their first order. We pay it forward so you can too.</div>
          </button>
        </div>

        {/* Share row */}
        <div className="mt-5 flex flex-col md:flex-row gap-3">
          <div className="flex-1 flex items-center rounded-full border-2 border-pink-200 bg-white px-4 h-12 min-w-0">
            <span className="text-xs uppercase font-bold tracking-widest text-stone-400 mr-3 shrink-0">Link</span>
            <span data-testid="referral-link" className="font-mono text-stone-900 text-sm truncate">{shareUrl.replace(/^https?:\/\//, "")}</span>
          </div>
          <Button data-testid="referral-copy-btn" variant="outline" onClick={copy} className="rounded-full h-12 border-stone-300 font-bold px-4">
            <Copy className="w-4 h-4 mr-2" /> Copy
          </Button>
          <Button data-testid="referral-qr-btn" variant="outline" onClick={() => setShowQR(true)} className="rounded-full h-12 border-stone-300 font-bold px-4">
            <QrCode className="w-4 h-4 mr-2" /> QR
          </Button>
          <Button data-testid="referral-share-btn" onClick={nativeShare} className="rounded-full h-12 bg-pink-500 hover:bg-pink-600 text-white font-bold px-5">
            <Share2 className="w-4 h-4 mr-2" /> Share
          </Button>
        </div>

        {/* Email / SMS invite row */}
        <div className="mt-3 flex flex-wrap gap-2">
          <Button data-testid="invite-email-btn" variant="ghost" size="sm" onClick={openEmailInvite} className="rounded-full text-stone-700 font-semibold hover:text-pink-600">
            <Mail className="w-4 h-4 mr-1.5" /> Invite by email
          </Button>
          <Button data-testid="invite-sms-btn" variant="ghost" size="sm" onClick={openSMSInvite} className="rounded-full text-stone-700 font-semibold hover:text-pink-600">
            <MessageSquare className="w-4 h-4 mr-1.5" /> Invite by SMS
          </Button>
        </div>
      </div>

      {/* Invite dialog */}
      {inviteOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setInviteOpen(false)} data-testid="invite-modal">
          <div className="bg-white rounded-3xl p-6 max-w-md w-full relative" onClick={(e) => e.stopPropagation()}>
            <button data-testid="invite-close" onClick={() => setInviteOpen(false)} className="absolute top-4 right-4 text-stone-400 hover:text-stone-800">
              <X className="w-5 h-5" />
            </button>
            <div className="font-heading text-2xl font-black tracking-tighter text-stone-900">
              {inviteType === "email" ? "Invite by email" : "Invite by SMS"}
            </div>
            <p className="text-sm text-stone-600 mt-1">
              We&rsquo;ll open your default {inviteType === "email" ? "email client" : "messaging app"} with the invite pre-filled.
            </p>
            <div className="mt-4">
              <Input
                data-testid="invite-input"
                autoFocus
                type={inviteType === "email" ? "email" : "tel"}
                placeholder={inviteType === "email" ? "friend@example.com" : "+1 555 000 0000"}
                value={inviteValue}
                onChange={(e) => setInviteValue(e.target.value)}
                className="h-12 rounded-xl"
              />
            </div>
            <Button data-testid="invite-send" onClick={sendInvite} className="mt-4 w-full h-12 rounded-full bg-pink-500 hover:bg-pink-600 text-white font-bold">
              Open {inviteType === "email" ? "email" : "messages"}
            </Button>
            <p className="text-xs text-stone-400 mt-3 text-center">
              Preview: <span className="italic">&ldquo;{messages.text.slice(0, 110)}…&rdquo;</span>
            </p>
          </div>
        </div>
      )}

      {/* QR dialog */}
      {showQR && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setShowQR(false)} data-testid="qr-modal">
          <div className="bg-white rounded-3xl p-8 max-w-sm w-full relative text-center" onClick={(e) => e.stopPropagation()}>
            <button data-testid="qr-close" onClick={() => setShowQR(false)} className="absolute top-4 right-4 text-stone-400 hover:text-stone-800">
              <X className="w-5 h-5" />
            </button>
            <div className="font-heading text-xl font-black tracking-tighter text-stone-900 mb-2">Scan to share</div>
            <p className="text-xs text-stone-500 mb-5">
              {mode === "gift" ? `They'll get $${data.gift_reward} off their first order.` : `You both get $${data.reward_per_referral}.`}
            </p>
            <div data-testid="qr-svg" className="p-4 bg-white rounded-2xl inline-block border border-pink-100">
              <QRCodeSVG value={shareUrl} size={220} level="M" fgColor="#1c1917" bgColor="#ffffff" />
            </div>
            <div className="mt-4 font-mono text-xs text-stone-500 break-all">{shareUrl}</div>
            <Button onClick={copy} variant="outline" className="mt-4 rounded-full h-10 border-stone-300 font-bold w-full">
              <Copy className="w-4 h-4 mr-2" /> Copy link
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}

function Stat({ icon, label, value, testid }) {
  return (
    <div className="flex items-center gap-2" data-testid={testid}>
      <div className="w-9 h-9 rounded-xl bg-pink-100 text-pink-600 flex items-center justify-center">{icon}</div>
      <div>
        <div className="text-[10px] uppercase font-bold tracking-widest text-stone-400">{label}</div>
        <div className="font-heading font-black text-stone-900 text-lg leading-none mt-0.5">{value}</div>
      </div>
    </div>
  );
}
